// Rich portfolio context for AI chatbot — sourced from profile.json
// Curated for chatbot use: narrative, experience, projects, skills, brand, goals

export const PortfolioData = {
  narrative: {
    elevatorPitch:
      "Hands-on digital systems and customer-focused operations professional with nearly four years of experience as the primary technical and digital operations point of contact in a small-organisation environment. Experience spans technical support, website and content administration, workflow improvement, stakeholder communication, and building live systems used by staff, parents, and community members. Currently completing a Certificate III in Information Technology and seeking opportunities across digital operations, customer experience, junior business analysis, product support, and customer-facing technology roles.",
    background:
      "My digital and technical career grew organically out of four years at St Catherine Labouré Catholic Church, where I became the primary technical and digital operations contact in a small office environment. I supported staff and volunteers across Microsoft 365, Google Workspace, Windows devices, websites, DNS, bookings, reporting tools, and day-to-day troubleshooting, while also improving workflows for a broader community operation. Alongside that, I built two live systems from scratch — a Windows desktop app (Mass Slide Builder) that automated weekly presentation creation and is still used 3+ years later, and a web-based Sacrament Enrolment System that processed 295 enrolments in its first year and replaced a fully manual paper process. After leaving that role I started KleinSoft, a small IT consulting and software business, where I rebuilt the Sacrament Enrolment System as a commercial product. Earlier in my career I worked in administration, data and reporting, communications, and community engagement roles, which strengthened my stakeholder communication, reporting, coordination, and customer-service instincts.",
    whyIT:
      "I gravitated into IT because I'm the person people naturally come to when something's broken. At St Catherine's, there was no official IT role — I just started fixing things, then administering systems, then building tools. I enjoyed it more than anything else I was doing, and I was good at it. The combination of problem-solving, helping people, and building things that actually get used is exactly what I want to do full-time.",
  },

  experience: [
    {
      jobTitle: "Independent Digital Systems & Customer Experience Projects",
      company: "KleinSoft",
      location: "Sydney, NSW",
      startDate: "Jan 2026",
      endDate: "Present",
      context:
        "Self-founded digital systems and support business. Currently the only employee. Maintains live platforms, improves workflows, provides technical support, and delivers internal tools for small organisations. First commercial client is St Pat's parish (Sacrament Enrolment System V2).",
      responsibilities: [
        "Maintain and improve live cloud-hosted systems used in real operational environments.",
        "Design and refine customer-facing and admin-facing workflows that reduce manual work and improve clarity for non-technical users.",
        "Provide technical support and digital solution delivery for local users and small organisations.",
        "Manage client relationships, scoping, invoicing, and delivery.",
        "Investigate and resolve production incidents, then communicate outcomes clearly to stakeholders.",
      ],
      keyAchievements: [
        "Secured first commercial contract (Sacrament Enrolment System V2) — live with 79 enrolments in first month.",
        "Investigated and resolved a live race condition in the enrolment system the same session it was reported — identified root cause, implemented fix, deployed to production, wrote post-incident notes.",
        "Designed Firestore audit trail system for enrolment data integrity.",
      ],
      toolsUsed: [
        "React 19",
        "TypeScript",
        "Firebase (Firestore, Auth, Functions, Hosting)",
        "Microsoft Graph API",
        "Node.js",
        "VS Code",
        "GitHub",
      ],
    },
    {
      jobTitle: "ICT Support & Systems Officer",
      company: "St Catherine Labouré Catholic Church",
      location: "Gymea, NSW",
      startDate: "Apr 2022",
      endDate: "Feb 2026",
      context:
        "Primary technical and digital operations contact in a small office environment serving a broader community organisation. Responsible for technical support, cloud administration, DNS, websites, event systems, and internal tool development, with limited internal resources and no dedicated IT team.",
      responsibilities: [
        "Administered Microsoft 365 tenant — user provisioning, licence management, Exchange mailboxes, SharePoint, Teams, MFA, security policies.",
        "Administered Google Workspace — user accounts, Drive, Classroom, Calendar, Shared Drives, admin console.",
        "Provided hands-on desktop and laptop support for Windows 10/11 devices — setup, imaging, troubleshooting, peripheral configuration, driver issues.",
        "Configured and maintained DNS records including MX, SPF, DKIM, and DMARC for church domain.",
        "Managed Wi-Fi and network connectivity issues across the church.",
        "Installed, configured, and maintained a Ubiquiti UniFi wireless network.",
        "Set up and supported AV equipment including projectors, sound systems, and microphones for church events (400+ attendees).",
        "Delivered end-user training and one-on-one guidance for non-technical staff.",
        "Managed Squarespace church website — content updates, DNS, integrations.",
        "Built and maintained Google Sheets-based operational dashboards and reporting tools for administration.",
        "Developed and maintained Google Apps Script automations.",
        "Used JIRA for bug tracking and internal issue management.",
        "Managed TryBooking platform for school event registrations and payments.",
      ],
      keyAchievements: [
        "Built Mass Slide Builder — a Python/Tkinter desktop app that automated weekly Mass presentation creation, saving ~2 hours per week. Still in active use 3+ years later.",
        "Built Sacrament Enrolment System V1 — processed 295 enrolments (239 confirmed) across three sacrament programs in its first year, replacing a fully manual paper-based process.",
        "Provided primary technical and digital operations support in a small-organisation setting, owning support, administration, and tooling end to end.",
        "Configured full DNS email authentication stack (MX, SPF, DKIM, DMARC) from scratch.",
        "Delivered end-user training to non-technical staff on new systems and workflows.",
      ],
      toolsUsed: [
        "Microsoft 365",
        "Google Workspace",
        "Windows 10/11",
        "Ubiquiti UniFi",
        "DNS (Cloudflare / cPanel)",
        "JIRA",
        "Google Apps Script",
        "Python",
        "Tkinter",
        "Squarespace",
        "TryBooking",
        "AnyDesk",
        "TeamViewer",
        "Excel",
        "Google Sheets",
      ],
    },
    {
      jobTitle: "Administrative Assistant",
      company: "Our Lady of the Rosary Church",
      location: "Fairfield, NSW",
      startDate: "Jan 2020",
      endDate: "Aug 2020",
      context:
        "Eight-month admin role during COVID-19. More hands-on than typical admin — created safety plans, set up an online booking system from scratch, did community outreach, and managed digital communications. First exposure to church/community administration.",
      keyAchievements: [
        "Implemented a COVID-19 safety plan and online booking system, enabling the parish to continue operating safely.",
        "Established a food services outreach program for struggling families.",
      ],
    },
    {
      jobTitle: "Data & Reporting Lead",
      company: "The Culture Project Australia",
      location: "Flemington, NSW",
      startDate: "Sep 2018",
      endDate: "Nov 2019",
      context:
        "NFP focused on youth culture. Responsible for data collection, analysis, reporting, stakeholder communication, and program delivery across schools and community audiences.",
      keyAchievements: [
        "Secured 35 recurring benefactors through one-to-one stakeholder meetings and relationship-based engagement, generating approximately $35,000 over 14 months.",
        "Built reporting dashboards improving stakeholder visibility into program performance.",
        "Delivered presentations and curriculum content as part of school and community program delivery.",
      ],
    },
  ],

  projects: [
    {
      name: "Sacrament Enrolment System (V2 — KleinSoft / St Pat's)",
      description:
        "Complete rebuild of the enrolment system as a commercial product for St Pat's parish. Improved UI, better admin tools, Microsoft Graph API integration for calendar sync, and Playwright for end-to-end testing. This is Laurence's first commercial KleinSoft contract and is actively in production.",
      techStack: ["React 19", "TypeScript", "Tailwind CSS", "Firebase", "Microsoft Graph API", "Playwright"],
      outcome:
        "Live with 79 enrolments in first month. Saving ~20 hours of admin per programme. First commercial contract for KleinSoft. Designed to scale to 300+ families annually across multiple sacrament programs.",
      recentlyShipped: [
        "Excel export with Names-Only and Full Export presets plus a customisable column selector.",
        "Prep programme sessions now display the full day of week (e.g. Wednesday 13 May).",
        "Simplified enrolment wizard — removed saint/sponsor and reconciliation date fields, to be collected via dedicated flows.",
        "Standardised mass attendance note wording across all email templates and parent views.",
      ],
      inDevelopment: [
        "Saint & Sponsor collection via a time-limited magic link email — parents submit saint/sponsor details and book a Confirmation Mass session without logging in. Uses Firestore transactions for capacity management.",
        "Confirmation Card PDF — printable card per child with saint name, sponsor, and session details. Individual and bulk download for admins.",
      ],
    },
    {
      name: "Sacrament Enrolment System (V1 — St Catherine's)",
      description:
        "Web-based enrolment system with parent-facing portal and admin dashboard for managing three sacrament programs (Reconciliation, First Communion, Confirmation). Replaced fully manual paper process. Included automated confirmation emails and reporting.",
      techStack: ["HTML", "CSS", "JavaScript", "Google Sheets API"],
      outcome: "Processed 295 enrolments (239 confirmed) in its first year. Eliminated manual paper processing entirely.",
    },
    {
      name: "KleinSoft One — Business Operations Platform",
      description:
        "Full-stack business operations platform for small businesses and sole traders. Unifies clients, invoicing, payments, expenses, tasks, and services into a single authenticated web app with live Stripe integration. Creating a client auto-provisions a Stripe Customer via Firebase Cloud Functions; defining a service creates a Stripe Product and Price. The Payments page merges live Stripe invoice data with legacy Firestore invoices. The dashboard pulls a real-time financial summary (paid, outstanding, overdue) from the Stripe API. PDF invoices can be generated with ABN, GST toggle, and bank details. Task management supports three views: Table, drag-and-drop Kanban board, and drag-and-drop Calendar (week/month). Includes a full onboarding flow and a Stripe Migration tool to bulk-migrate existing clients and services.",
      techStack: ["Next.js 15", "TypeScript", "Firebase", "Stripe API", "Tailwind CSS v4", "Radix UI", "Zustand", "React Hook Form", "Zod", "Cloud Functions", "Google Cloud Secret Manager", "@react-pdf/renderer", "@dnd-kit"],
      outcome:
        "Functional full-stack SaaS product demonstrating live third-party API integration (Stripe), serverless backend architecture, and complex UI patterns including drag-and-drop and dynamic PDF generation.",
    },
    {
      name: "KleinSoft Ops — Field Operations Android App",
      description:
        "Purpose-built Android app for independent IT support technicians. Handles the full job lifecycle on a single device — from arrival to payment collection. Features a live visit timer that survives app backgrounding and device restarts, automated billing calculations ($60 flat for the first hour, then $15 per 15-minute increment), and flexible payment collection via Stripe Tap to Pay, email invoice, or SMS/WhatsApp payment link. Includes built-in troubleshooting guides and an offline-first architecture with a SharedPreferences job queue that syncs automatically on reconnection. All Stripe interactions are handled server-side via Cloud Functions — secret keys never touch the client. Firebase App Check is enforced on all callable functions.",
      techStack: ["Flutter", "Dart", "Firebase Firestore", "Firebase Cloud Functions (TypeScript)", "Firebase App Check", "Stripe (Tap to Pay, Invoices, Payment Links)", "Google Cloud (australia-southeast1)", "Material 3"],
      outcome:
        "Fully functional Android app demonstrating full-stack mobile development, Stripe payment integration, offline-first architecture, and serverless security patterns.",
    },
    {
      name: "Mass Slide Builder",
      description:
        "A Python/Tkinter desktop tool built to automate recurring slide preparation for weekly services. Staff enter structured service content and the tool generates a complete presentation ready for operational use.",
      techStack: ["Python", "Tkinter", "python-pptx", "python-docx", "PyInstaller", "Inno Setup"],
      outcome: "Reduced weekly slide preparation time by ~50% (saving ~2 hours/week). Documented, handed over, and used consistently for 3+ years in a live operational setting.",
    },
    {
      name: "FlexiAI v2 — Multi-Model AI Chat Platform",
      description:
        "Pay-as-you-go AI chat platform connecting OpenAI GPT-4, Anthropic Claude Opus, and Google Gemini Pro through a single interface. Features mid-conversation model switching, credit-based billing, Stripe sandbox integration, and vector-based semantic memory using OpenAI Embeddings stored in Firebase.",
      techStack: [
        "React",
        "TypeScript",
        "Vite",
        "Firebase",
        "OpenAI GPT-4 API",
        "Anthropic Claude API",
        "Google Gemini API",
        "OpenAI Embeddings API",
        "Stripe (Sandbox)",
      ],
      outcome:
        "Functional MVP with working multi-model chat, credit system, and semantic memory. Demonstrates significant multi-vendor API architecture without AI-written boilerplate — all architecture decisions made by Laurence.",
    },
    {
      name: "Soldiers of Christ Fitness — Training Web App",
      description:
        "Custom web app for a personal training business. Client profiles, workout logging with progressive overload tracking, trainer dashboard for managing multiple clients remotely. Built pre-AI using AppGyver/FlutterFlow and Firebase — all manual research and problem-solving.",
      techStack: ["AppGyver (Low-Code IDE)", "FlutterFlow", "Firebase"],
      outcome:
        "Deployed and used by 5 active paying clients over 2+ years. Proves ability to architect and ship a real product independently before AI tools existed.",
    },
    {
      name: "AWS To-Do List App",
      description:
        "React-based task management app built for hands-on AWS experience. Full serverless backend with Lambda, API Gateway, and DynamoDB.",
      techStack: ["React", "AWS Lambda", "API Gateway", "DynamoDB"],
      outcome: "Working deployed app. Practical experience with cloud infrastructure, serverless architecture, and API security.",
    },
    {
      name: "Resume & Cover Letter Builder",
      description:
        "React + TypeScript web app for building, versioning, and exporting resumes and cover letters. Multi-version support, PDF and DOCX export, auto-save, inline editing, version-specific tailoring.",
      techStack: ["React", "TypeScript", "Node.js", "Express", "@react-pdf/renderer", "docx"],
      outcome: "Active personal tool used during current job search.",
    },
  ],

  skills: {
    coreITSupport: [
      "Level 1/2 Incident Troubleshooting & Resolution",
      "End-User Technical Support (On-site & Remote)",
      "Hardware & Software Troubleshooting",
      "Windows 10/11 Configuration & Support",
      "Laptop & Workstation Setup & Configuration",
      "Multi-Monitor & Peripheral Setup",
      "Printer Installation & Troubleshooting",
      "Network & Wi-Fi Connectivity Troubleshooting",
      "Application Installation, Configuration & Support",
      "Remote Desktop Support (AnyDesk, TeamViewer)",
      "Data Backup & Recovery",
    ],
    cloudAndSystems: [
      "Microsoft 365 Administration",
      "Google Workspace Administration",
      "User Provisioning & Access Management",
      "Licence Assignment & Management",
      "Email & Mailbox Configuration & Troubleshooting",
      "Shared Calendar & Collaboration Setup",
      "MFA & Security Policy Configuration",
      "Active Directory (Basic)",
      "DNS & SSL Configuration",
      "MX, SPF, DKIM, DMARC Setup",
    ],
    automationAndDevelopment: [
      "Google Apps Script Automation",
      "Python Automation Scripting",
      "PowerShell / CMD Scripting",
      "Internal Workflow Automation",
      "React / TypeScript / JavaScript (AI-assisted)",
      "Firebase (Firestore, Auth, Functions, Hosting)",
      "AWS (Lambda, API Gateway, DynamoDB)",
      "Google APIs (Sheets, Calendar, Gmail, Apps Script)",
      "REST APIs",
      "Git / GitHub",
    ],
    ticketingAndITSM: [
      "JIRA — Bug & Issue Tracking",
      "Freshdesk — Helpdesk Ticketing & SLA Management",
      "Incident Triage & Root Cause Analysis",
      "Vendor Escalation",
      "SOP & Documentation Creation",
    ],
    userTraining: [
      "End-User Training & Workshops",
      "Technical Guidance for Non-Technical Users",
      "User Onboarding Support",
      "Plain-English Technical Communication",
      "Stakeholder Reporting",
    ],
  },

  education: [
    {
      degree: "Certificate III in Information Technology",
      institution: "TAFE NSW",
      status: "In progress — expected completion 2026",
      details: "Covers networking fundamentals, hardware, operating systems, and IT support best practices.",
    },
    {
      degree: "Bachelor of Computer Science",
      institution: "University of Sydney",
      status: "Coursework completed — not formally conferred",
      details:
        "Covered programming fundamentals, systems architecture, database management, algorithms, and software engineering.",
    },
    {
      degree: "Bachelor of Arts (Philosophy & Theology)",
      institution: "University of Notre Dame Australia",
      status: "Completed",
      details:
        "Strengthened critical thinking, written communication, and the ability to break down complex concepts — valuable in technical support and client-facing roles.",
    },
  ],

  personalBrand: {
    strengths: [
      "Sole-operator IT experience — comfortable being the only tech person in the building.",
      "Strong customer service instinct — naturally patient and approachable with non-technical users.",
      "Self-starter who builds solutions when off-the-shelf tools don't fit.",
      "Combines hands-on troubleshooting with workflow, systems, and content support capability.",
      "Experienced across the full user support lifecycle from intake to resolution.",
      "Comfortable in community, NFP, education, and faith-based environments.",
      "Strong communication and stakeholder-management background alongside technical delivery.",
      "Understands trade-offs between technologies and can make informed decisions.",
      "Knows Git branching, staging/production environments, CI/CD concepts, and modern development workflows.",
    ],
    softSkills: [
      "Patient and empathetic communicator",
      "Strong written and verbal communication",
      "Self-motivated and autonomous",
      "Detail-oriented and thorough",
      "Calm under pressure (live events, urgent issues)",
      "Adaptable — comfortable learning new systems quickly",
      "Honest and trustworthy with sensitive data",
    ],
    workStyle:
      "I operate as the architect and product owner when building tools — I make every architecture and design decision, set the build order, give UX/UI feedback, and course-correct. Before AI coding tools existed, I was doing the same thing using visual/low-code platforms (AppGyver, FlutterFlow). Same brain, better tools. In support contexts I'm methodical and systematic — I work the problem through rather than guessing, and I document what I find.",
    uniqueSellingPoints: [
      "Nearly 4 years as the primary technical and digital operations contact in a live office environment — real support and workflow experience, not theory.",
      "Built two production systems from scratch (Enrolment System, Mass Slide Builder) — shows ability to improve processes as well as support users.",
      "Combines technical support, stakeholder communication, reporting, and digital operations experience in one profile.",
      "BA + Computer Science coursework = strong communication AND technical foundation.",
      "Already running a consulting business (KleinSoft) — entrepreneurial self-starter.",
      "Comfortable in faith-based, education, and NFP sectors — understands the culture and constraints.",
    ],
    honestWeaknesses: [
      "Relies on AI-assisted development tools (like Copilot/Claude) for rapid code generation, focusing my independent effort on system architecture, logic, and deployment.",
      "No formal IT certifications yet (Cert III in progress).",
      "Limited enterprise ticketing systems experience (ServiceNow, Zendesk, Jira SM).",
      "No formal ITIL training.",
      "Limited corporate/enterprise IT exposure — experience is primarily community/NFP.",
      "Limited Active Directory hands-on experience.",
      "Would need ramp-up on enterprise Windows Server and Group Policy.",
    ],
  },

  careerGoals: {
    targetRoles: [
      "Digital Operations Coordinator",
      "Customer Experience Coordinator",
      "Junior Business Analyst",
      "Product Support Coordinator",
      "IT Support Officer",
      "ICT Support Officer",
      "Service Desk Analyst",
      "Helpdesk Support Technician",
      "Level 1/2 IT Support",
      "Desktop Support Technician",
      "IT Systems Administrator",
      "Junior Systems Administrator",
    ],
    targetIndustries: [
      "Education (Schools, Universities)",
      "Not-for-Profit / Community Organisations",
      "Church / Religious Organisations",
      "Government / Public Sector",
      "Healthcare",
      "Small-Medium Business",
      "IT Traineeship Provider",
      "MSP (Managed Service Provider)",
    ],
    salaryExpectation: "Open to discussion based on scope and role requirements.",
    location:
      "Sydney — ideally Southern Sydney / Sutherland Shire / St George area, open to Parramatta/Western Sydney if the role is right. Prefer under 60 min commute from Kirrawee.",
    availability: "Immediately / 1–2 weeks notice",
    workPreference: "Flexible (open to hybrid, on-site, or remote)",
  },

  recruiterQA: [
    {
      question: "Tell me about yourself.",
      answer:
        "Laurence is a hands-on digital systems and customer-focused operations professional with nearly four years of experience as the primary technical and digital operations contact in a live office environment. His background spans technical support, workflow improvement, bookings, reporting tools, website updates, stakeholder communication, and building live systems used by staff and community members. More recently, through KleinSoft, he has been maintaining and improving a cloud-hosted enrolment platform while also providing independent technical support and digital solution delivery. He is currently completing a Certificate III in IT and is targeting roles across digital operations, customer experience, junior business analysis, product support, and customer-facing technology teams.",
    },
    {
      question: "Why IT? How did you get into it?",
      answer:
        "Honestly, it grew organically. At St Catherine's there was no dedicated IT role — I just became the person people came to when something was broken. Over time that turned into administering 365 and Google Workspace, configuring DNS, setting up UniFi Wi-Fi, and eventually building tools like the Mass Slide Builder and the Sacrament Enrolment System. I realised I enjoyed it more than anything else I was doing, and I was good at it. The combination of problem-solving, helping people, and building things that actually get used is exactly what I want to do full time.",
    },
    {
      question: "What's your greatest strength?",
      answer:
        "Staying calm and working the problem systematically when things go wrong. I've managed live production systems with real users depending on them — a race condition in the enrolment system mid-enrolment season, DNS issues affecting email deliverability for the whole school, AV failures at events with 400+ people in the room. In those situations I don't guess — I work through it methodically, document what I find, and fix it. I also naturally translate technical problems into plain English, which is something non-technical users genuinely appreciate.",
    },
    {
      question: "What's your greatest weakness?",
      answer:
        "Formal enterprise IT exposure. My experience is deep but it comes from a school and small-business context — not a large corporate environment. I haven't worked with ServiceNow or enterprise-scale Active Directory, and I haven't had formal ITIL training. I'm aware of that gap, which is why I'm doing the Cert III, and I'm confident I can ramp up quickly — I've learned every system from scratch so far.",
    },
    {
      question: "Why should we hire you?",
      answer:
        "Because Laurence has already operated in a role that combined support, systems, communication, and process improvement under limited resources. He has supported non-technical users, maintained digital platforms, improved workflows, built internal tools, managed stakeholder communication, and delivered practical solutions end to end. That breadth means he can contribute beyond a narrow job description while still staying grounded and realistic about where he is strongest.",
    },
    {
      question: "Where do you want to be in five years?",
      answer:
        "The immediate goal is finishing my Cert III and moving into a structured role where I can keep building across digital operations, support, workflow improvement, and stakeholder-facing delivery. Over time, I'd like to grow into a role with broader ownership across systems, operational improvement, and tooling — ideally one that lets me keep combining technical problem-solving with process improvement and user support.",
    },
    {
      question: "What salary are you looking for?",
      answer:
        "I'm open to discussing salary based on the scope of the role — the priority for me right now is getting formal industry experience in a reputable team, not maximising pay.",
    },
    {
      question: "What's your availability? When can you start?",
      answer:
        "Immediately, or within one to two weeks if needed.",
    },
    {
      question: "How will you manage KleinSoft if you take a full-time role?",
      answer:
        "KleinSoft is currently structured around low-maintenance, deployed systems (like the Enrolment System) that require minimal ongoing support. My priority is securing a full-time role, and I will manage any residual KleinSoft commitments strictly outside of business hours, ensuring zero impact on my primary employer.",
    },
    {
      question: "Do you have any certifications?",
      answer:
        "I'm currently completing my Certificate III in Information Technology through TAFE — expected completion in 2026. I also have an Excel Essential Training certificate from LinkedIn Learning. No vendor certifications yet, but I'm planning to move into CompTIA A+ or Microsoft certifications once the Cert III is done.",
    },
    {
      question: "Tell me about a difficult technical problem you solved.",
      answer:
        "The most notable one was a live race condition in the Sacrament Enrolment System. During active enrolment season, a parent reported they'd completed the form but their child wasn't showing up in the admin dashboard. I investigated the Firestore data and found that two near-simultaneous submissions were overwriting each other — the last write was winning regardless of which submission was valid. I traced it to a missing transaction wrapper around the write operation, implemented a Firestore transaction to make it atomic, deployed the fix the same session, and wrote up a post-incident note with the root cause and what I'd changed. No data loss, no further incidents.",
    },
    {
      question: "How do you handle a frustrated or non-technical user?",
      answer:
        "With patience and plain English. Most frustration comes from people feeling like they don't understand what's happening or that their problem isn't being taken seriously. I focus on making them feel heard first, then explaining what I'm doing without jargon, and giving them a realistic timeline. I've supported teachers and admin staff who had very low technical confidence — the key is never making someone feel stupid for not knowing something.",
    },
    {
      question: "Walk me through how you'd troubleshoot a user who can't send email.",
      answer:
        "First I'd confirm the scope — is it just this user, or are others affected? That narrows it to a user-specific config issue vs a broader mail flow or DNS problem. For a single user I'd check their account status in 365 or Google Workspace, verify their mailbox is active and not over quota, and check if there's a specific bounce message — the NDR usually tells you exactly what's wrong. If it's an authentication issue I'd check MFA and app passwords. If it looks like a mail flow problem I'd check the MX records and SPF/DKIM/DMARC configuration. I've done full DNS email auth setup from scratch, so I'm comfortable reading those records.",
    },
    {
      question: "What experience do you have with Active Directory?",
      answer:
        "My AD experience is basic — user provisioning, group management, and some group policy work in a small school environment. It's not enterprise-scale AD, and I'd be honest about that. I have strong foundational knowledge of how it works and have done the account lifecycle management side (creating, disabling, resetting), but I haven't managed complex OU structures or multi-domain environments. It's an area I'd expect to ramp up on in a role that requires it.",
    },
    {
      question: "Have you worked in an MSP environment before?",
      answer:
        "Not formally — KleinSoft is the closest thing, where I'm supporting a client across a deployed cloud environment. But I haven't worked in a traditional MSP with a ticket queue, SLAs, and a team around me. I'm aware of what that environment looks like and I'm focused on finding a structured IT role — whether that's at an MSP or elsewhere — where I can build that team experience. The core support skills are there; the formal context is what I'm seeking to add.",
    },
  ],

  github: "https://github.com/Laurence2400",
  portfolio: "https://laurence-portfolio.web.app",
};
